import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ReminderTimeSelect } from '@/components/atoms/ReminderTimeSelect/ReminderTimeSelect';
import { updateAction, updateNode } from '@/redux/nodes/actions';
import { ReminderAction } from '@/shared/types/action';
import { convertMinutesToSeconds, formatSecondsToReadable } from '@/util/dateUtils';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { reminderActionSchema } from '@/util/validation/schemas/action-schemas';

interface Props {
  action: ReminderAction;
}

interface Form {
  reminder_seconds: number;
}

export const InactivityTimeout = ({ action }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Backward compatibility: some actions may still have 'minutes' field from legacy data
  const legacyAction = action as ReminderAction & { minutes?: number };
  const seconds = action.reminder_seconds ?? convertMinutesToSeconds(legacyAction.minutes ?? 0);
  const { trigger_node_id } = action;

  const { control } = useForm<Form>({
    values: { reminder_seconds: seconds },
    resolver: createYupResolver<Form>(reminderActionSchema),
  });

  return (
    <div className="flex flex-col gap-1">
      <Controller
        name="reminder_seconds"
        control={control}
        render={({ field, fieldState }) => (
          <>
            <ReminderTimeSelect
              value={field.value}
              onChange={(value) => {
                const reminder_seconds = parseInt(value);
                field.onChange(reminder_seconds);

                dispatch(
                  updateAction({
                    actionId: action.action_id,
                    action: { reminder_seconds, minutes: undefined },
                  })
                );

                // Update the respective Reminder node
                dispatch(
                  updateNode({
                    nodeId: trigger_node_id,
                    node: {
                      name: `${t('common.after')} ${formatSecondsToReadable(reminder_seconds)}`,
                    },
                  })
                );
              }}
              hasError={!!fieldState.error}
              placeholder={t('ai_agents.reminder.select_duration')}
            />
            {fieldState.error && (
              <p className="ml-1 text-xs leading-5 font-normal text-destructive">
                {fieldState.error.message}
              </p>
            )}
          </>
        )}
      />
    </div>
  );
};
